import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { markJobMaterialsInstalled, replaceJobMaterialsSnapshot } from "@/services/inventory.service";
import { SubcontractorAssignmentService } from "@/services/subcontractor-assignment.service";
import { JobLocationService } from "@/services/job-location.service";
import { isExternalManagedImport } from "@/data/import/migrationVisibilityGuard";
import { SystemUpdateService } from "@/services/system-update.service";
import type {
  Job,
  JobStage,
  MaterialReadiness,
  AssignmentType,
  PermitStatus,
  OrderStatus,
  MaterialsStatus,
  DispatchStatus,
  ProcurementStatus,
  WarehouseStatus,
  CloseoutStatus,
  RescissionStatus,
  ProjectType,
  ActivityEntry,
  OrderLineItem,
  MaterialLineItem,
  JobFinancials,
  PermitDetail,
  MarketingAttribution,
} from "@/data/types";
 import { toUiJob, toDbJobCreate } from "@/mappers/job.mapper";
 import type { AllocationStatus } from "@/data/WarehouseInventoryContext";
 import { deriveBoundSpecForJob } from "@/services/bound-spec.service";
 import { resolveJobChecklistAsync, syncJobChecklistItems } from "@/services/job-checklist.service";

function valueMatchesDesired(current: unknown, desired: unknown): boolean {
  if (desired === undefined) return true;
  if (desired === null) return current == null;
  if (typeof desired === "object") {
    try {
      return JSON.stringify(current ?? null) === JSON.stringify(desired);
    } catch {
      return false;
    }
  }
  return current === desired;
}

function activityHistoryKey(entry: ActivityEntry): string {
  const id = String(entry.id ?? "").trim();
  if (id) return `id:${id}`;
  return [
    String(entry.date ?? "").trim(),
    String(entry.action ?? "").trim(),
    String(entry.user ?? "").trim(),
    String(entry.noteType ?? "").trim(),
  ].join("|");
}

function mergeActivityHistory(
  existing: ActivityEntry[] | undefined,
  incoming: ActivityEntry[] | undefined,
): ActivityEntry[] {
  const merged: ActivityEntry[] = [];
  const seen = new Set<string>();

  for (const entry of [...(existing ?? []), ...(incoming ?? [])]) {
    if (!entry) continue;
    const key = activityHistoryKey(entry);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(entry);
  }

  return merged;
}

function jobSatisfiesPatch(job: Job, patch: Partial<Job>): boolean {
  for (const [key, desired] of Object.entries(patch) as Array<[keyof Job, unknown]>) {
    if (!valueMatchesDesired((job as unknown as Record<string, unknown>)[key], desired)) {
      return false;
    }
  }
  return true;
}

function isUuid(val: string | undefined | null): val is string {
  if (!val) return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val);
}

const JOB_BY_ID_CACHE_TTL_MS = 10_000;
const jobByIdCache = new Map<string, { at: number; data: Job | null; error: unknown }>();
const jobByIdInFlight = new Map<string, Promise<{ data: Job | null; error: unknown }>>();

function jobLookupCacheKey(value: string): string {
  const column = isUuid(value) ? "id" : "job_number";
  return `${column}:${value}`;
}

function rememberJobLookup(job: Job | null): void {
  if (!job) return;
  const result = { at: Date.now(), data: job, error: null };
  if (job.id) jobByIdCache.set(jobLookupCacheKey(job.id), result);
  if (job.jobNumber) jobByIdCache.set(jobLookupCacheKey(job.jobNumber), result);
}

function forgetJobLookup(value: string): void {
  jobByIdCache.delete(jobLookupCacheKey(value));
  jobByIdInFlight.delete(jobLookupCacheKey(value));
}

function shouldSyncInstalledOnTransition(current: Job, updates: Partial<Job>): boolean {
  if (updates.currentStage === "Completed" && current.currentStage !== "Completed") return true;
  if (updates.installStatus === "Install Complete" && current.installStatus !== "Install Complete") return true;
  return false;
}

function hasRealMaterialLines(job: Pick<Job, "materialsList">): boolean {
  const rows = Array.isArray(job.materialsList) ? job.materialsList : [];
  return rows.some((row) => {
    const sku = String(row.sku ?? "").trim();
    const name = String(row.itemName ?? "").trim();
    const qty = Number(row.quantityRequired ?? 0);
    return (sku.length > 0 || name.length > 0) && qty > 0;
  });
}

function isJobNumberUniqueViolation(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const rec = error as Record<string, unknown>;
  const code = String(rec.code ?? "");
  const message = String(rec.message ?? "").toLowerCase();
  const detail = String(rec.details ?? "").toLowerCase();
  if (code !== "23505") return false;
  return (
    message.includes("job_number") ||
    message.includes("idx_jobs_org_job_number") ||
    detail.includes("job_number")
  );
}

function normalizeImportedJobNumber(val: string | undefined | null): string {
  const raw = String(val ?? "").trim();
  if (!raw) return "";
  return raw.toUpperCase().replace(/\s+/g, "-");
}

function nextSequentialJobNumber(lastNum: number): string {
  return `JOB-${String(lastNum + 1).padStart(3, "0")}`;
}

function incrementJobNumber(base: string, step: number): string {
  const raw = String(base || "").trim();
  if (!raw) return raw;
  const m = raw.match(/^(.*?)-(\d+)$/);
  if (m) {
    const prefix = m[1];
    const current = Number(m[2]) || 0;
    return `${prefix}-${current + step}`;
  }
  return `${raw}-${step + 1}`;
}

function escapeLikePattern(input: string): string {
  return input.replace(/[%_]/g, "\\$&");
}

function nextSuffixFromSeries(base: string, values: string[]): string {
  let max = 0;
  const prefix = `${base}-`;
  for (const value of values) {
    const v = String(value ?? "").trim().toUpperCase();
    if (v === base.toUpperCase()) continue;
    if (!v.startsWith(prefix.toUpperCase())) continue;
    const suffix = v.slice(prefix.length);
    const n = Number(suffix);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return `${base}-${max + 1}`;
}

async function resolveNextImportedJobNumber(
  organizationId: string,
  baseJobNumber: string,
  sourceSystem?: string,
  externalProjectId?: string,
): Promise<string> {
  const pattern = `${escapeLikePattern(baseJobNumber)}%`;

  const [jobsRes, importRowsRes] = await Promise.all([
    supabase
      .from("jobs")
      .select("job_number")
      .eq("organization_id", organizationId)
      .ilike("job_number", pattern),
    supabase
      .from("job_import_rows")
      .select("payload, external_project_id, source_system")
      .eq("organization_id", organizationId),
  ]);

  const jobNumbersFromJobs = (jobsRes.data ?? []).map((row) =>
    String((row as { job_number?: string | null }).job_number ?? ""),
  );

  const rowPayloadNumbers = (importRowsRes.data ?? [])
    .filter((row) => {
      const typed = row as {
        external_project_id?: string | null;
        source_system?: string | null;
      };
      // Avoid counting the current staged import row itself.
      if (
        externalProjectId &&
        sourceSystem &&
        typed.external_project_id === externalProjectId &&
        String(typed.source_system ?? "").toLowerCase() === sourceSystem.toLowerCase()
      ) {
        return false;
      }
      return true;
    })
    .map((row) => {
      const payload = (row as { payload?: unknown }).payload;
      if (!payload || typeof payload !== "object") return "";
      return String((payload as { jobNumber?: string }).jobNumber ?? "");
    })
    .filter((v) => v.toUpperCase().startsWith(baseJobNumber.toUpperCase()));

  return nextSuffixFromSeries(baseJobNumber, [...jobNumbersFromJobs, ...rowPayloadNumbers]);
}

async function getLatestNativeJobSequence(organizationId: string): Promise<number> {
  const { data: rows } = await supabase
    .from("jobs")
    .select("job_number")
    .eq("organization_id", organizationId)
    .ilike("job_number", "JOB-%")
    .not("job_number", "ilike", "JOB-IMP-%");

  let max = 0;
  for (const row of rows ?? []) {
    const val = String((row as { job_number?: string | null }).job_number ?? "").trim().toUpperCase();
    const m = val.match(/^JOB-(\d+)$/);
    if (!m) continue;
    const n = Number(m[1]);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max;
}

const INSTALL_STAGE_GUARDED: ReadonlySet<JobStage> = new Set([
  "Assigned and Dispatched",
  "In Progress",
]);

function hasInstallAssignee(job: Job): boolean {
  return Boolean(
    String(job.assignedCrew ?? "").trim() ||
    String(job.assignedSubcontractor ?? "").trim() ||
    String(job.crewId ?? "").trim(),
  );
}

function hasInstallSchedule(job: Job): boolean {
  return Boolean(
    String(job.scheduledInstallDate ?? "").trim() ||
    String(job.targetInstallDate ?? "").trim() ||
    String(job.scheduledStartTime ?? "").trim(),
  );
}

async function hasMaterialAllocationForInstall(jobId: string): Promise<boolean> {
  const activeAllocationStates: AllocationStatus[] = ["Reserved", "Ready for Install"];
  const { data, error } = await supabase
    .from("job_allocations")
    .select("id")
    .eq("job_id", jobId)
    .in("status", activeAllocationStates)
    .limit(1);
  if (error) {
    console.warn("[JobService] install-stage allocation check failed:", error.message);
    return false;
  }
  return Array.isArray(data) && data.length > 0;
}

async function assertInstallStagePrereqs(
  jobId: string,
  current: Job,
  updates: Partial<Job>,
): Promise<Error | null> {
  const targetStage = updates.currentStage;
  if (!targetStage || !INSTALL_STAGE_GUARDED.has(targetStage)) return null;

  const next = { ...current, ...updates };
  if (!hasInstallAssignee(next)) {
    return new Error("Install stage blocked: crew/contractor must be assigned first");
  }
  if (!hasInstallSchedule(next)) {
    return new Error("Install stage blocked: install date/time must be scheduled first");
  }
  const hasAllocation = await hasMaterialAllocationForInstall(jobId);
  if (!hasAllocation) {
    return new Error("Install stage blocked: material allocations are missing");
  }
  return null;
}

// ── DB row shape — actual columns confirmed in the Supabase DB ────────────
// All business fields are stored in the metadata JSONB column.
// Only a small set of top-level columns exist alongside it.
export interface JobRow {
  id: string;
  organization_id?: string | null;
  source_meta?: Job["sourceMeta"] | null;
  current_stage?: string | null;
  title?: string | null;
  job_type?: string | null;
  job_number: string | null;
  customer_id?: string | null;     // FK → customers.id
  contractor_id?: string | null;   // FK → contractors.id
  crew_id?: string | null;         // FK → crews.id
  customer_name?: string | null;   // direct column fallback (older schemas)
   // Embedded join result when queried with customers(...)
   customers?: {
     first_name: string | null;
     last_name: string | null;
     address_line1?: string | null;
     address_line2?: string | null;
     city: string | null;
     state: string | null;
     postal_code?: string | null;
   } | null;
   // Embedded join result when queried with contractors(...)
   contractors?: {
     company_name: string | null;
   } | null;
  // Canonical DB columns in current schema.
  scheduled_start_at?: string | null;
  scheduled_end_at?: string | null;
  // Legacy compatibility aliases (older code paths/tests may still reference these).
  schedule_start_at?: string | null;
  schedule_end_at?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  material_readiness?: string | null;
  permit_required?: boolean;
  notes?: string | null;
  metadata: JobMetadata | null;
  created_at: string;
  updated_at: string;
  ntbo_status?: string | null;
  home_type?: string | null;
  plumbing_permit_status?: string | null;
  parcel_number?: string | null;
  permit_expectation_date?: string | null;
  drawing_floor_plan_completed?: boolean | null;
  second_bathroom_drawing_completed?: boolean | null;
  ready_for_inspection?: boolean | null;
  homeowner_available_date?: string | null;
  inspection_confirmed?: boolean | null;
}

type JobMaterialRow = {
  job_id: string | null;
  sku: string | null;
  item_name: string | null;
  quantity_required: number | null;
  quantity_allocated: number | null;
  quantity_staged: number | null;
  quantity_installed: number | null;
  missing_items: number | null;
  field_purchase: boolean | null;
  inventory_assignment: string | null;
  lifecycle_stage: string | null;
  inventory_status: string | null;
  unit_cost_snapshot: number | null;
  unit_price_snapshot: number | null;
  prices_snapshot: number | null;
};

const JOB_LIST_SELECT = [
  "id",
  "organization_id",
  "source_meta",
  "current_stage",
  "title",
  "job_type",
  "job_number",
  "customer_id",
  "contractor_id",
  "crew_id",
  "scheduled_start_at",
  "scheduled_end_at",
  "address_line1",
  "address_line2",
  "city",
  "state",
  "postal_code",
  "material_readiness",
  "permit_required",
  "notes",
  "metadata",
  "created_at",
  "updated_at",
  "ntbo_status",
  "home_type",
  "plumbing_permit_status",
  "parcel_number",
  "permit_expectation_date",
  "drawing_floor_plan_completed",
  "second_bathroom_drawing_completed",
  "ready_for_inspection",
  "homeowner_available_date",
  "inspection_confirmed",
].join(", ");

// Backward-compatible select for environments whose jobs table has not yet
// received the newer top-level columns (schedule/address/material/permit fields).
const JOB_BASE_SELECT = [
  "id",
  "organization_id",
  "source_meta",
  "current_stage",
  "title",
  "job_type",
  "job_number",
  "customer_id",
  "contractor_id",
  "crew_id",
  "metadata",
  "created_at",
  "updated_at",
].join(", ");

// ── Metadata shape — all business fields live here ────────────────────────
export interface JobMetadata {
  sourceMeta?: Job["sourceMeta"];
  source_meta?: Job["sourceMeta"];
  // Identity / display
  customerName?: string;
  phone?: string;
  email?: string;
  secondaryContactName?: string;
  secondaryContactPhone?: string;
  secondaryContactEmail?: string;

  // Project
  projectType?: ProjectType;
  scopeSummary?: string;
  description?: string;
  payoutAmount?: number;
  offerCompensationConfig?: Job["offerCompensationConfig"];

  // Stage & statuses
  currentStage?: JobStage;
  permitStatus?: PermitStatus;
  permitDetail?: PermitDetail;
  orderStatus?: OrderStatus;
  materialsStatus?: MaterialsStatus;
  dispatchStatus?: DispatchStatus;
  contractorAssignmentStatus?: string;
  assignedCrew?: string;
  assignmentType?: AssignmentType;
  assignedSubcontractor?: string;
  assignedSubcontractorId?: string;
  crewId?: string;

  // Material detail
  materialReadinessNotes?: string;
  missingItemsList?: string;
  procurementStatus?: ProcurementStatus;
  warehouseStatus?: WarehouseStatus;
  orderDetail?: OrderLineItem[];
  materialsList?: MaterialLineItem[];
  fieldMaterials?: Job["fieldMaterials"];
  unmappedSkusForReview?: Job["unmappedSkusForReview"];

  // Closeout
  closeoutStatus?: CloseoutStatus;
  finalDocumentationComplete?: boolean;
  customerSignoff?: boolean;
  closeoutNotes?: string;
  closeoutSignoff?: Job["closeoutSignoff"];

  // Flags
  issueFlag?: boolean;
  qaSubmitted?: boolean;
  scopeLocked?: boolean;
  intakeApproved?: boolean;
  approvedForOrdering?: boolean;

  // Financials
  financials?: JobFinancials;

  // Workflow
  opportunityId?: string;
  appointmentResultId?: string;
  planningStatus?: Job["planningStatus"];
  orderType?: Job["orderType"];
  selectedVendor?: string;
  selectedVendorType?: string;
  schedulingStatus?: Job["schedulingStatus"];
  installReadiness?: Job["installReadiness"];
  jobMaterialStatus?: Job["jobMaterialStatus"];
  installStatus?: Job["installStatus"];
  qaStatus?: Job["qaStatus"];

  // Rescission
  rescissionStatus?: RescissionStatus;
  rescissionEndsAt?: string;
  convertedFromQuoteId?: string;

  // Hold
  holdStatus?: string;
  holdReason?: string;

  // Multi-tenant
  branch?: string;
  region?: string;
  salesRep?: string;

  // Activity & media
  activityHistory?: ActivityEntry[];
  photos?: string[];

  // Marketing
  marketing?: MarketingAttribution;

  // Config
  projectConfig?: Record<string, unknown>;

  // Review & referral
  reviewSent?: boolean;
  reviewCompleted?: boolean;
  reviewSatisfied?: boolean | null;
  reviewSentAt?: string;
  referralRequested?: boolean;
  referralReceived?: boolean;
  referralRequestedAt?: string;
  googleReviewUrl?: string;

// Schedule detail
   targetInstallDate?: string;
   scheduledStartTime?: string;
   scheduledEndTime?: string;
   scheduledShifts?: number;
   timeSlot?: string;
   timezone?: string;
   externalCalendarEventId?: string;
   buildReviewChecklist?: Record<string, boolean>;
   qaReviewerChecklist?: Record<string, boolean>;
   boundJobSpec?: Job["boundJobSpec"];
   bound_job_spec?: Job["boundJobSpec"];

   // Change orders
   changeOrders?: Job["changeOrders"];

   // Checklist (installer portal)
   checklist?: Job["checklist"];

   // Photo groups
   photoGroups?: Job["photoGroups"];

   // Contractor rating
   contractorRatingSent?: boolean;
   contractorRatingUrl?: string;
   contractorRatingAt?: string;
   contractorRatingCompleted?: boolean;

    // Contract document (imported/uploaded PDF)
    contractDocument?: Job["contractDocument"];

    // Import / migration
    migrationImportDetail?: Job["migrationImportDetail"];
  }

// ── Stage patch — fields that change during stage transitions ─────────────
export interface JobStagePatch {
  currentStage?: JobStage;
  dispatchStatus?: DispatchStatus;
  assignmentType?: AssignmentType;
  assignedSubcontractor?: string;
  assignedCrew?: string;
  crewId?: string;
  schedulingStatus?: Job["schedulingStatus"];
  contractorAssignmentStatus?: string;
  holdStatus?: string;
  holdReason?: string;
  planningStatus?: Job["planningStatus"];
  scopeLocked?: boolean;
  intakeApproved?: boolean;
  approvedForOrdering?: boolean;
  buildReviewChecklist?: Record<string, boolean>;
}

async function resolveCustomerIdForJob(job: Job): Promise<string | null> {
  const explicitCustomerId = (job as Job & { customerId?: string }).customerId;
  if (explicitCustomerId && typeof explicitCustomerId === "string") {
    return explicitCustomerId;
  }

  const quoteRef = typeof job.convertedFromQuoteId === "string"
    ? job.convertedFromQuoteId.trim()
    : "";
  if (!quoteRef) return null;

  // Prefer canonical quote UUID lookup.
  const byId = await supabase
    .from("quotes")
    .select("customer_id")
    .eq("id", quoteRef)
    .maybeSingle();
  if (!byId.error && byId.data?.customer_id) {
    return byId.data.customer_id;
  }

  // Fallback for environments still passing quote_number references.
  const byNumber = await supabase
    .from("quotes")
    .select("customer_id")
    .eq("quote_number", quoteRef)
    .maybeSingle();
  if (!byNumber.error && byNumber.data?.customer_id) {
    return byNumber.data.customer_id;
  }

  return null;
}

function deriveJobTitle(job: Job): string {
  const project = (job.projectType || "").toString().trim();
  const customer = (job.customerName || "").toString().trim();
  if (project && customer) return `${customer} - ${project}`;
  if (project) return project;
  if (customer) return customer;
  return "Untitled Job";
}

function deriveReadinessFromJobMaterials(
  current: Job["materialReadiness"],
  materials: Job["materialsList"],
): Job["materialReadiness"] {
  if (!materials.length) return current;
  const normalizedCurrent = String(current ?? "").trim();
  if (isTerminalMaterialReadiness(normalizedCurrent)) {
    return normalizedCurrent as Job["materialReadiness"];
  }

  const hasMissing = materials.some((m) => Math.max(0, Number(m.missingItems ?? 0)) > 0);
  if (hasMissing) return "Missing Items";

  const totalRequired = materials.reduce((acc, m) => acc + Math.max(0, Number(m.quantityRequired ?? 0)), 0);
  const totalAllocated = materials.reduce((acc, m) => acc + Math.max(0, Number(m.quantityAllocated ?? 0)), 0);
  const anyStagedOrInstalled = materials.some(
    (m) =>
      Math.max(0, Number(m.quantityStaged ?? 0)) > 0 ||
      Math.max(0, Number(m.quantityInstalled ?? 0)) > 0,
  );

  if (totalRequired > 0 && totalAllocated >= totalRequired) {
    return anyStagedOrInstalled ? "Staged for Job" : "Fully Received";
  }

  return current;
}

function isTerminalMaterialReadiness(value: unknown): boolean {
  return ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(String(value ?? "").trim());
}

function suppressStaleMissingMaterials(
  materials: Job["materialsList"],
  materialReadiness: Job["materialReadiness"],
): Job["materialsList"] {
  if (!isTerminalMaterialReadiness(materialReadiness)) return materials;
  return materials.map((material) => ({
    ...material,
    missingItems: 0,
    fieldPurchase: false,
  }));
}

async function syncAuthoritativeJobMaterialsSnapshot(
  job: Pick<Job, "id" | "jobNumber" | "customerName" | "currentStage" | "materialsList">,
): Promise<unknown | null> {
  try {
    await replaceJobMaterialsSnapshot(job);
    return null;
  } catch (error) {
    console.error("[JobService] Failed to sync authoritative job_materials snapshot:", error);
    return error;
  }
}

function hydrateJobsFromJobMaterials(rows: Job[], materialsRows: JobMaterialRow[]): Job[] {
  const byJobId = new Map<string, Job["materialsList"]>();
  for (const mat of materialsRows) {
    const jobId = String(mat.job_id ?? "").trim();
    const sku = String(mat.sku ?? "").trim();
    if (!jobId || !sku) continue;
    const line = {
      sku,
      itemName: String(mat.item_name ?? sku).trim() || sku,
      quantityRequired: Math.max(0, Number(mat.quantity_required ?? 0)),
      quantityAllocated: Math.max(0, Number(mat.quantity_allocated ?? 0)),
      quantityStaged: Math.max(0, Number(mat.quantity_staged ?? 0)),
      quantityInstalled: Math.max(0, Number(mat.quantity_installed ?? 0)),
      missingItems: Math.max(0, Number(mat.missing_items ?? 0)),
      unitCost: Number(mat.unit_cost_snapshot ?? mat.prices_snapshot ?? 0),
      taxPercent: Number.isFinite(Number((mat as { tax_percent?: unknown }).tax_percent))
        ? Math.max(0, Number((mat as { tax_percent?: unknown }).tax_percent))
        : undefined,
      unitPrice: Number(mat.unit_price_snapshot ?? mat.prices_snapshot ?? 0),
      fieldPurchase: Boolean(mat.field_purchase),
      inventoryAssignment: (mat.inventory_assignment === "Assigned" ? "Assigned" : "Stock") as Job["materialsList"][number]["inventoryAssignment"],
      lifecycleStage: (mat.lifecycle_stage ?? "Ordered") as Job["materialsList"][number]["lifecycleStage"],
      inventoryStatus: (mat.inventory_status ?? "In Stock") as Job["materialsList"][number]["inventoryStatus"],
    };
    const list = byJobId.get(jobId) ?? [];
    list.push(line);
    byJobId.set(jobId, list);
  }

  return rows.map((job) => {
    const canonical = byJobId.get(job.id);
    if (!canonical || canonical.length === 0) return job;
    const materialsList = suppressStaleMissingMaterials(canonical, job.materialReadiness);
    const missingItemsList = materialsList
      .filter((m) => m.missingItems > 0)
      .map((m) => m.itemName)
      .join(", ");
    return {
      ...job,
      materialsList,
      missingItemsList: missingItemsList || "",
      materialReadiness: deriveReadinessFromJobMaterials(job.materialReadiness, materialsList),
    };
  });
}

async function hydrateJobsWithMaterialRows(jobs: Job[]): Promise<Job[]> {
  const jobIds = jobs.map((j) => j.id).filter(Boolean);
  if (jobIds.length === 0) return jobs;

  const { data: materialsRows, error: materialsError } = await supabase
    .from("job_materials")
    .select("job_id, sku, item_name, quantity_required, quantity_allocated, quantity_staged, quantity_installed, missing_items, field_purchase, inventory_assignment, lifecycle_stage, inventory_status, unit_cost_snapshot, unit_price_snapshot, prices_snapshot")
    .in("job_id", jobIds);

  if (materialsError || !materialsRows) return jobs;
  return hydrateJobsFromJobMaterials(jobs, materialsRows as JobMaterialRow[]);
}

async function resolveCrewIdByName(
  organizationId: string | null | undefined,
  crewName: string | null | undefined,
): Promise<string | null> {
  const orgId = String(organizationId ?? "").trim();
  const name = String(crewName ?? "").trim();
  if (!orgId || !name) return null;
  const { data, error } = await supabase
    .from("crews")
    .select("id")
    .eq("organization_id", orgId)
    .ilike("name", name)
    .maybeSingle();
  if (error || !data?.id) return null;
  return String(data.id);
}

async function resolveContractorIdByName(
  organizationId: string | null | undefined,
  contractorName: string | null | undefined,
): Promise<string | null> {
  const normalize = (value: string) => value.trim().toLowerCase().replace(/\s+/g, " ");
  const orgId = String(organizationId ?? "").trim();
  const name = String(contractorName ?? "").trim();
  if (!orgId || !name) return null;
  const { data, error } = await supabase
    .from("contractors")
    .select("id, company_name, status")
    .eq("organization_id", orgId)
    .ilike("company_name", name)
    .limit(25);
  if (error || !Array.isArray(data) || data.length === 0) return null;

  const target = normalize(name);
  const sorted = (data as Array<{ id?: string | null; company_name?: string | null; status?: string | null }>)
    .filter((row) => typeof row.id === "string" && row.id.trim().length > 0)
    .sort((a, b) => {
      const aExact = normalize(String(a.company_name ?? "")) === target ? 1 : 0;
      const bExact = normalize(String(b.company_name ?? "")) === target ? 1 : 0;
      if (aExact !== bExact) return bExact - aExact;
      const aActive = String(a.status ?? "").toLowerCase() === "active" ? 1 : 0;
      const bActive = String(b.status ?? "").toLowerCase() === "active" ? 1 : 0;
      if (aActive !== bActive) return bActive - aActive;
      return String(a.id).localeCompare(String(b.id));
    });

  return sorted.length > 0 ? String(sorted[0].id) : null;
}

function splitCustomerNameForDb(name: string | null | undefined): { first_name: string; last_name: string | null } {
  const cleaned = String(name ?? "").trim().replace(/\s+/g, " ");
  if (!cleaned) return { first_name: "", last_name: null };
  const parts = cleaned.split(" ");
  if (parts.length === 1) return { first_name: parts[0], last_name: null };
  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(" "),
  };
}

function buildCustomerPatchFromJobUpdates(updates: Partial<Job>, merged: Job): Record<string, unknown> | null {
  const patch: Record<string, unknown> = {};

  if ("customerName" in updates) {
    Object.assign(patch, splitCustomerNameForDb(merged.customerName));
  }
  if ("phone" in updates) {
    patch.phone = String(merged.phone ?? "").trim() || null;
  }
  if ("email" in updates) {
    patch.email = String(merged.email ?? "").trim() || null;
  }
  if ("address" in updates) {
    patch.address_line1 = merged.address?.street?.trim() || null;
    patch.address_line2 = null;
    patch.city = merged.address?.city?.trim() || null;
    patch.state = merged.address?.state?.trim() || null;
    patch.postal_code = merged.address?.zip?.trim() || null;
  }

  return Object.keys(patch).length > 0 ? patch : null;
}

async function syncLinkedCustomerFromJobUpdates(
  currentRow: JobRow,
  updates: Partial<Job>,
  merged: Job,
): Promise<unknown | null> {
  const customerId = String(currentRow.customer_id ?? "").trim();
  if (!isUuid(customerId)) return null;

  const patch = buildCustomerPatchFromJobUpdates(updates, merged);
  if (!patch) return null;

  const { error } = await supabase
    .from("customers")
    .update(patch)
    .eq("id", customerId);

  if (error) {
    console.error("[JobService.update] linked customer sync failed:", JSON.stringify(error));
    return error;
  }

  return null;
}

// ── Service ───────────────────────────────────────────────────────────────
export const JobService = {
  async getAll(): Promise<{ data: Job[] | null; error: unknown }> {
    const orgId = await getCurrentOrganizationId();
    const { data, error } = await supabase
      .from("jobs")
      .select(`${JOB_LIST_SELECT}, customers(first_name, last_name, address_line1, address_line2, city, state, postal_code)`)
      .eq("organization_id", orgId)
      .order("created_at", { ascending: false });

    if (error) {
      // Graceful fallback for older schemas missing one or more new columns.
      const fallback = await supabase
        .from("jobs")
        .select(`${JOB_BASE_SELECT}, customers(first_name, last_name, address_line1, address_line2, city, state, postal_code)`)
        .eq("organization_id", orgId)
        .order("created_at", { ascending: false });
      if (fallback.error || !fallback.data) return { data: null, error: fallback.error || error };
      const jobs = await hydrateJobsWithMaterialRows((fallback.data as unknown as JobRow[]).map(toUiJob));
      return { data: jobs, error: null };
    }
    if (!data) return { data: null, error };
    const jobs = await hydrateJobsWithMaterialRows((data as unknown as JobRow[]).map(toUiJob));
    return { data: jobs, error: null };
  },

  async getUpdatedSince(sinceIso: string, limit = 500): Promise<{ data: Job[] | null; error: unknown }> {
    const since = String(sinceIso ?? "").trim();
    if (!since) return this.getAll();
    const sinceMs = Date.parse(since);
    // Small overlap protects against timestamp precision ties; client merge is idempotent.
    const safeSince = Number.isNaN(sinceMs)
      ? since
      : new Date(Math.max(0, sinceMs - 1_000)).toISOString();

    const orgId = await getCurrentOrganizationId();
    const fetchUpdated = (select: string) =>
      supabase
        .from("jobs")
        .select(`${select}, customers(first_name, last_name, address_line1, address_line2, city, state, postal_code)`)
        .eq("organization_id", orgId)
        .gt("updated_at", safeSince)
        .order("updated_at", { ascending: true })
        .limit(limit);

    const { data, error } = await fetchUpdated(JOB_LIST_SELECT);
    if (error) {
      const fallback = await fetchUpdated(JOB_BASE_SELECT);
      if (fallback.error || !fallback.data) return { data: null, error: fallback.error || error };
      const jobs = await hydrateJobsWithMaterialRows((fallback.data as unknown as JobRow[]).map(toUiJob));
      return { data: jobs, error: null };
    }
    if (!data) return { data: null, error };
    const jobs = await hydrateJobsWithMaterialRows((data as unknown as JobRow[]).map(toUiJob));
    return { data: jobs, error: null };
  },

  async getById(jobNumber: string): Promise<{ data: Job | null; error: unknown }> {
    const key = jobLookupCacheKey(jobNumber);
    const cached = jobByIdCache.get(key);
    if (cached && Date.now() - cached.at < JOB_BY_ID_CACHE_TTL_MS) {
      return { data: cached.data, error: cached.error };
    }
    const inFlight = jobByIdInFlight.get(key);
    if (inFlight) return inFlight;

    const run = (async () => {
    const column = isUuid(jobNumber) ? "id" : "job_number";
    const { data, error } = await supabase
      .from("jobs")
      .select(`${JOB_LIST_SELECT}, customers(first_name, last_name, address_line1, address_line2, city, state, postal_code)`)
      .eq(column, jobNumber)
      .limit(1);

    if (error) {
      const fallback = await supabase
        .from("jobs")
        .select(`${JOB_BASE_SELECT}, customers(first_name, last_name, address_line1, address_line2, city, state, postal_code)`)
        .eq(column, jobNumber)
        .limit(1);
      if (fallback.error || !fallback.data || (Array.isArray(fallback.data) && fallback.data.length === 0)) {
        return { data: null, error: fallback.error || error };
      }
      const rowData = Array.isArray(fallback.data) ? fallback.data[0] : fallback.data;
      if (!rowData) return { data: null, error: fallback.error || error };
      const job = toUiJob(rowData as unknown as JobRow);
      rememberJobLookup(job);
      return { data: job, error: null };
    }
    // Handle both array (from .limit(1)) and object cases
    const rowData = Array.isArray(data) ? data[0] : data;
    if (!rowData) return { data: null, error };
    const job = toUiJob(rowData as unknown as JobRow);
    rememberJobLookup(job);
    return { data: job, error: null };
    })();

    jobByIdInFlight.set(key, run);
    try {
      return await run;
    } finally {
      jobByIdInFlight.delete(key);
    }
  },

  async getByStage(stage: JobStage): Promise<{ data: Job[] | null; error: unknown }> {
    const { data, error } = await this.getAll();
    if (error || !data) return { data: null, error };
    return { data: data.filter((j) => j.currentStage === stage), error: null };
  },

  async create(job: Job): Promise<{ data: Job | null; error: unknown }> {
    const [organizationId, customerId] = await Promise.all([
      getCurrentOrganizationId(),
      resolveCustomerIdForJob(job),
    ]);

    if (!customerId) {
      return {
        data: null,
        error: new Error("Unable to resolve customer_id for job creation. Ensure the quote is linked to a customer."),
      };
    }

    // Native sequence: strictly JOB-# only (exclude JOB-IMP-*).
    const lastNum = await getLatestNativeJobSequence(organizationId);
    let sequentialCursor = lastNum;
    const importedJobNumber = normalizeImportedJobNumber(job.jobNumber);
    let candidateJobNumber = importedJobNumber || nextSequentialJobNumber(sequentialCursor);
    if (importedJobNumber) {
      candidateJobNumber = await resolveNextImportedJobNumber(
        organizationId,
        importedJobNumber,
        job.sourceMeta?.sourceSystem,
        job.sourceMeta?.externalProjectId,
      );
    }

    // Ensure boundJobSpec includes cost/price when materials are provided.
    // External-managed imports must never auto-synthesize a spec from fallback data.
    let jobForCreate = { ...job, organizationId, checklist: await resolveJobChecklistAsync({ ...job, organizationId }) };
    if (!job.boundJobSpec && hasRealMaterialLines(job) && !isExternalManagedImport(job)) {
      const boundSpec = deriveBoundSpecForJob(job);
      if (boundSpec) {
        jobForCreate = { ...jobForCreate, boundJobSpec: boundSpec };
      }
    }

    let latestError: unknown = null;
    for (let attempt = 0; attempt < 12; attempt++) {
      const row = {
        ...toDbJobCreate(jobForCreate),
        job_number: candidateJobNumber,
        organization_id: organizationId,
        source_meta: jobForCreate.sourceMeta ?? null,
        customer_id: customerId,
        title: deriveJobTitle(job),
      };

      const { data, error } = await supabase
        .from("jobs")
        .insert([row])
        .select()
        .single();

      if (!error && data) {
        const createdJob = toUiJob(data as JobRow);
        if (jobForCreate.materialsList !== undefined) {
          const materialSyncError = await syncAuthoritativeJobMaterialsSnapshot({
            id: createdJob.id,
            jobNumber: createdJob.jobNumber,
            customerName: createdJob.customerName,
            currentStage: createdJob.currentStage,
            materialsList: createdJob.materialsList,
          });
          if (materialSyncError) return { data: null, error: materialSyncError };
        }
        const checklistSync = await syncJobChecklistItems({
          ...createdJob,
          organizationId: createdJob.organizationId ?? organizationId,
          checklist: jobForCreate.checklist,
        });
        if (checklistSync.error) return { data: null, error: checklistSync.error };
        // Fire-and-forget geocoding — failure must not block job creation
        const addr = createdJob.address;
        if (createdJob.id && addr) {
          const fullAddress = [addr.street, addr.city, addr.state, addr.zip]
            .filter(Boolean)
            .join(", ");
          if (fullAddress) {
            JobLocationService.geocodeJob(createdJob.id, fullAddress).catch((err) =>
              console.warn("[job.service] geocodeJob failed (non-blocking):", err)
            );
          }
        }
        const label = (createdJob.jobNumber ?? createdJob.id ?? 'New Job') as string
        void SystemUpdateService.post({
          domain: 'jobs',
          event:  'job_created',
          body:   `System: Job Created — ${label}`,
        })
        return { data: { ...createdJob, checklist: jobForCreate.checklist }, error: null };
      }

      latestError = error;
      if (!isJobNumberUniqueViolation(error)) {
        return { data: null, error };
      }

      // Collision strategy:
      // - imported job_number present: query same-org series and use next suffix
      // - local sequential generation: bump sequence and retry
      if (importedJobNumber) {
        candidateJobNumber = await resolveNextImportedJobNumber(
          organizationId,
          importedJobNumber,
          job.sourceMeta?.sourceSystem,
          job.sourceMeta?.externalProjectId,
        );
      } else {
        sequentialCursor += 1;
        candidateJobNumber = nextSequentialJobNumber(sequentialCursor);
      }
    }

    return { data: null, error: latestError ?? new Error("Failed to generate unique job_number") };
  },

  async update(id: string, updates: Partial<Job>): Promise<{ data: Job | null; error: unknown }> {
    forgetJobLookup(id);
    // Fetch current row to merge metadata cleanly
    const column = isUuid(id) ? "id" : "job_number";
    const { data: current, error: fetchError } = await supabase
      .from("jobs")
      .select("*")
      .eq(column, id)
      .limit(1);

    // Handle both array (from .limit(1)) and object cases, with duplicate warning
    let currentRow: JobRow | undefined;
    if (fetchError) {
      return { data: null, error: fetchError };
    }
    if (Array.isArray(current)) {
      if (current.length > 1) {
        console.warn(`[JobService.update] Multiple jobs found for ${column}=${id}, using first match.`);
      }
      currentRow = current[0] as JobRow | undefined;
    } else {
      currentRow = current as JobRow | undefined;
    }
    if (!currentRow) return { data: null, error: fetchError };

    const currentJob = toUiJob(currentRow);
    const updatesForMerge: Partial<Job> = { ...updates };
    if (updatesForMerge.activityHistory !== undefined) {
      updatesForMerge.activityHistory = mergeActivityHistory(
        currentJob.activityHistory,
        updatesForMerge.activityHistory,
      );
    }
    const currentJobId = String(currentRow.id ?? "");

    const explicitCrewIdRaw = String(updates.crewId ?? "").trim();
    const explicitCrewId = isUuid(explicitCrewIdRaw) ? explicitCrewIdRaw : "";
    const explicitAssignedCrew = String(updates.assignedCrew ?? "").trim();
    const explicitAssignmentType = String(updates.assignmentType ?? "").trim();
    const explicitAssignedSubIdRaw = String(updates.assignedSubcontractorId ?? "").trim();
    const explicitAssignedSubId = isUuid(explicitAssignedSubIdRaw) ? explicitAssignedSubIdRaw : "";
    const explicitAssignedSubName = String(updates.assignedSubcontractor ?? "").trim();

    // Canonical crew link normalization:
    // - Internal Crew + assignedCrew -> resolve crew_id from crews table when caller didn't pass crewId.
    // - Subcontractor/Unassigned (or cleared assignedCrew) -> clear crew_id.
    if (explicitCrewId) {
      updatesForMerge.crewId = explicitCrewId;
    } else if (explicitCrewIdRaw) {
      // Ignore non-UUID ids from fallback UI lists; resolve canonical UUID by name below.
      updatesForMerge.crewId = "";
    } else if (explicitAssignmentType === "Internal Crew" && explicitAssignedCrew) {
      const resolvedCrewId = await resolveCrewIdByName(
        currentRow.organization_id,
        explicitAssignedCrew,
      );
      if (resolvedCrewId) updatesForMerge.crewId = resolvedCrewId;
    } else if (
      explicitAssignmentType === "Subcontractor" ||
      explicitAssignmentType === "Unassigned" ||
      ("assignedCrew" in updates && !explicitAssignedCrew)
    ) {
      updatesForMerge.crewId = "";
    }

    // Canonical subcontractor link normalization:
    // - Subcontractor assignment -> resolve/keep assignedSubcontractorId.
    // - Internal Crew / Unassigned -> clear assignedSubcontractorId.
    if (explicitAssignmentType === "Subcontractor") {
      if (explicitAssignedSubId) {
        updatesForMerge.assignedSubcontractorId = explicitAssignedSubId;
      } else if (explicitAssignedSubIdRaw) {
        updatesForMerge.assignedSubcontractorId = "";
      } else if (explicitAssignedSubName) {
        const resolvedContractorId = await resolveContractorIdByName(
          currentRow.organization_id,
          explicitAssignedSubName,
        );
        if (resolvedContractorId) updatesForMerge.assignedSubcontractorId = resolvedContractorId;
      }
    } else if (
      explicitAssignmentType === "Internal Crew" ||
      explicitAssignmentType === "Unassigned" ||
      ("assignedSubcontractor" in updates && !explicitAssignedSubName)
    ) {
      updatesForMerge.assignedSubcontractorId = "";
    }

     const shouldSyncChecklist = Object.prototype.hasOwnProperty.call(updates, "checklist");
     const merged = { ...currentJob, ...updatesForMerge };
     if (shouldSyncChecklist) {
       merged.checklist = await resolveJobChecklistAsync(merged);
     }

     // If materialsList is being updated, recompute boundJobSpec to reflect latest cost/price.
     // Skip for external-managed imports to avoid generating synthetic spec/BOM.
     if (updates.materialsList !== undefined && !isExternalManagedImport(merged) && hasRealMaterialLines(merged)) {
       const boundJobSpec = deriveBoundSpecForJob(merged);
       if (boundJobSpec) {
         merged.boundJobSpec = boundJobSpec;
       }
     }

     const full = toDbJobCreate(merged);

    // Only send columns we know exist in the DB.
    // Business fields remain in `metadata`, while core operational mirrors
    // (scheduled_* + material/permit/notes) are kept in sync for fast reads.
    const safePatch: Record<string, unknown> = {
      metadata: full.metadata,
      job_type: full.job_type,
      scheduled_start_at: full.scheduled_start_at ?? null,
      scheduled_end_at: full.scheduled_end_at ?? null,
      material_readiness: full.material_readiness,
      permit_required: full.permit_required,
      notes: full.notes,
      source_meta: merged.sourceMeta ?? null,
    };
    if ("title" in updatesForMerge || "customerName" in updatesForMerge || "projectType" in updatesForMerge) {
      safePatch.title = deriveJobTitle(merged as Job);
    }
    if ("address" in updatesForMerge) {
      safePatch.address_line1 = full.address_line1 ?? null;
      safePatch.address_line2 = full.address_line2 ?? null;
      safePatch.city = full.city ?? null;
      safePatch.state = full.state ?? null;
      safePatch.postal_code = full.postal_code ?? null;
    }
    if ("crewId" in updatesForMerge) {
      const crewIdRaw = String(updatesForMerge.crewId ?? "").trim();
      safePatch.crew_id = isUuid(crewIdRaw) ? crewIdRaw : null;
    }
    if ("assignedSubcontractorId" in updatesForMerge || updates.assignmentType !== undefined) {
      const contractorIdRaw = String(updatesForMerge.assignedSubcontractorId ?? "").trim();
      safePatch.contractor_id = isUuid(contractorIdRaw) ? contractorIdRaw : null;
    }

    // Sync the dedicated current_stage column so stage-filtered queries stay accurate.
    // toUiJob prefers db.current_stage over metadata.currentStage, so this must be kept in sync.
    if (updates.currentStage !== undefined) safePatch.current_stage = updates.currentStage;

    if (updates.ntboStatus !== undefined) safePatch.ntbo_status = updates.ntboStatus || null;
    if (updates.homeType !== undefined) safePatch.home_type = updates.homeType || null;
    if (updates.plumbingPermitStatus !== undefined) safePatch.plumbing_permit_status = updates.plumbingPermitStatus || null;
    if (updates.parcelNumber !== undefined) safePatch.parcel_number = updates.parcelNumber || null;
    if (updates.permitExpectationDate !== undefined) safePatch.permit_expectation_date = updates.permitExpectationDate || null;
    if (updates.drawingFloorPlanCompleted !== undefined) safePatch.drawing_floor_plan_completed = updates.drawingFloorPlanCompleted || null;
    if (updates.secondBathroomDrawingCompleted !== undefined) safePatch.second_bathroom_drawing_completed = updates.secondBathroomDrawingCompleted || null;
    if (updates.readyForInspection !== undefined) safePatch.ready_for_inspection = updates.readyForInspection || null;
    if (updates.homeownerAvailableDate !== undefined) safePatch.homeowner_available_date = updates.homeownerAvailableDate || null;
    if (updates.inspectionConfirmed !== undefined) safePatch.inspection_confirmed = updates.inspectionConfirmed || null;

    const installGuardError = await assertInstallStagePrereqs(currentJobId, currentJob, updatesForMerge);
    if (installGuardError) {
      console.warn("[JobService.update] install transition blocked:", installGuardError.message);
      return { data: null, error: installGuardError };
    }

    // Optimistic concurrency guard:
    // only apply this patch if updated_at is still what we fetched.
    // If another writer updated the row in-between, refetch and retry once.
    const applyPatch = async (expectedUpdatedAt: string | null | undefined) => {
      let query = supabase
        .from("jobs")
        .update(safePatch)
        .eq(column, id);
      if (expectedUpdatedAt) query = query.eq("updated_at", expectedUpdatedAt);
      const { data, error } = await query.select();
      return { data: (data as JobRow[] | null) ?? null, error };
    };

    let write = await applyPatch(currentRow.updated_at);
    if (!write.error && write.data && write.data.length > 0) {
      const customerSyncError = await syncLinkedCustomerFromJobUpdates(currentRow, updatesForMerge, merged);
      if (customerSyncError) {
        return { data: null, error: customerSyncError };
      }
      if (shouldSyncInstalledOnTransition(currentJob, updatesForMerge)) {
        void markJobMaterialsInstalled(currentJobId);
      }
      const job = toUiJob(write.data[0]);
      if (updates.materialsList !== undefined) {
        const materialSyncError = await syncAuthoritativeJobMaterialsSnapshot(job);
        if (materialSyncError) return { data: null, error: materialSyncError };
      }
      if (shouldSyncChecklist) {
        const checklistSync = await syncJobChecklistItems({
          ...job,
          organizationId: job.organizationId ?? currentRow.organization_id ?? undefined,
          checklist: merged.checklist,
        });
        if (checklistSync.error) return { data: null, error: checklistSync.error };
      }
      rememberJobLookup(job);
      if (updates.stage && ['Completed', 'Project Closed', 'completed', 'project_closed'].includes(String(updates.stage))) {
        void SystemUpdateService.post({
          domain:   'jobs',
          event:    'job_closed',
          body:     `System: Job Closed Out — ${id}`,
          metadata: {
            title:  'Job Closed Out',
            detail: `Job ${id}`,
            action: { label: 'View Job', path: `/jobs/${id}` },
          },
        })
      }
      const jobLabel = job.jobNumber ?? id;
      if (updates.scheduledInstallDate && String(updates.scheduledInstallDate).trim()) {
        void SystemUpdateService.post({
          domain:   'schedule',
          event:    'install_scheduled',
          body:     `System: Install Scheduled — ${jobLabel}`,
          metadata: {
            title:  'Install Scheduled',
            detail: `${jobLabel}`,
            action: { label: 'View Schedule', path: '/dispatch-intelligence' },
          },
        })
      }
      if (updates.assignedCrew && String(updates.assignedCrew).trim()) {
        void SystemUpdateService.post({
          domain:   'schedule',
          event:    'crew_assigned',
          body:     `System: Crew Assigned to Job — ${jobLabel}`,
          metadata: {
            title:  'Crew Assigned',
            detail: `Job ${jobLabel}`,
            action: { label: 'View Job', path: `/jobs/${currentJobId}` },
          },
        })
      }
      return { data: { ...job, checklist: merged.checklist }, error: null };
    }

    // Potential lost-update race: refetch latest row and retry once with a
    // freshly merged metadata patch against the newest snapshot.
    const { data: latest, error: latestErr } = await supabase
      .from("jobs")
      .select("*")
      .eq(column, id)
      .single();
    if (latestErr || !latest) {
      const fallbackError = write.error ?? latestErr;
      console.error("[JobService.update] conflict refetch failed:", JSON.stringify(fallbackError));
      return { data: null, error: fallbackError };
    }

    const latestJob = toUiJob(latest as JobRow);
    const latestUpdatesForMerge: Partial<Job> = { ...updatesForMerge };
    if (latestUpdatesForMerge.activityHistory !== undefined) {
      latestUpdatesForMerge.activityHistory = mergeActivityHistory(
        latestJob.activityHistory,
        latestUpdatesForMerge.activityHistory,
      );
    }
    const latestMerged = { ...latestJob, ...latestUpdatesForMerge, checklist: merged.checklist };
    const remapped = toDbJobCreate(latestMerged);
    safePatch.metadata = remapped.metadata;
    safePatch.job_type = remapped.job_type;
    safePatch.scheduled_start_at = remapped.scheduled_start_at ?? null;
    safePatch.scheduled_end_at = remapped.scheduled_end_at ?? null;
    safePatch.material_readiness = remapped.material_readiness;
    safePatch.permit_required = remapped.permit_required;
    safePatch.notes = remapped.notes;
    safePatch.source_meta = (latestJob.sourceMeta ?? updatesForMerge.sourceMeta ?? null);
    if ("address" in updatesForMerge) {
      safePatch.address_line1 = remapped.address_line1 ?? null;
      safePatch.address_line2 = remapped.address_line2 ?? null;
      safePatch.city = remapped.city ?? null;
      safePatch.state = remapped.state ?? null;
      safePatch.postal_code = remapped.postal_code ?? null;
    }

    write = await applyPatch((latest as JobRow).updated_at);
    if (write.error || !write.data || write.data.length === 0) {
      if (write.error) {
        console.error("[JobService.update] retry write error:", JSON.stringify(write.error));
        return { data: null, error: write.error };
      }

      const { data: newest, error: newestErr } = await supabase
        .from("jobs")
        .select("*")
        .eq(column, id)
        .single();
      if (!newestErr && newest) {
        const newestJob = toUiJob(newest as JobRow);
        if (jobSatisfiesPatch(newestJob, updatesForMerge)) {
          const customerSyncError = await syncLinkedCustomerFromJobUpdates(currentRow, updatesForMerge, {
            ...newestJob,
            ...updatesForMerge,
          });
          if (customerSyncError) {
            return { data: null, error: customerSyncError };
          }
          if (updates.materialsList !== undefined) {
            const materialSyncError = await syncAuthoritativeJobMaterialsSnapshot(newestJob);
            if (materialSyncError) return { data: null, error: materialSyncError };
          }
          if (shouldSyncChecklist) {
            const checklistSync = await syncJobChecklistItems({
              ...newestJob,
              organizationId: newestJob.organizationId ?? currentRow.organization_id ?? undefined,
              checklist: merged.checklist,
            });
            if (checklistSync.error) return { data: null, error: checklistSync.error };
          }
          rememberJobLookup(newestJob);
          return { data: { ...newestJob, checklist: merged.checklist }, error: null };
        }
      } else if (newestErr) {
        console.error("[JobService.update] post-race refetch failed:", JSON.stringify(newestErr));
      }

      console.warn(
        "[JobService.update] retry write matched 0 rows and desired state not observed; returning conflict",
      );
      return { data: null, error: new Error("Concurrent update conflict") };
    }
    const customerSyncError = await syncLinkedCustomerFromJobUpdates(currentRow, updatesForMerge, {
      ...toUiJob(write.data[0]),
      ...updatesForMerge,
    });
    if (customerSyncError) {
      return { data: null, error: customerSyncError };
    }
    if (shouldSyncInstalledOnTransition(currentJob, updatesForMerge)) {
      void markJobMaterialsInstalled(currentJobId);
    }
    const job = toUiJob(write.data[0]);
    if (updates.materialsList !== undefined) {
      const materialSyncError = await syncAuthoritativeJobMaterialsSnapshot(job);
      if (materialSyncError) return { data: null, error: materialSyncError };
    }
    if (shouldSyncChecklist) {
      const checklistSync = await syncJobChecklistItems({
        ...job,
        organizationId: job.organizationId ?? currentRow.organization_id ?? undefined,
        checklist: merged.checklist,
      });
      if (checklistSync.error) return { data: null, error: checklistSync.error };
    }
    rememberJobLookup(job);
    return { data: { ...job, checklist: merged.checklist }, error: null };
  },

  /**
   * Lightweight stage transition — fetches only `metadata`, merges patch fields,
   * then writes `current_stage` + updated `metadata` in a single update call.
   */
  async updateStage(jobNumber: string, patch: JobStagePatch): Promise<{ error: unknown }> {
    forgetJobLookup(jobNumber);
    const column = isUuid(jobNumber) ? "id" : "job_number";
    const { data: current, error: fetchError } = await supabase
      .from("jobs")
      .select("*")
      .eq(column, jobNumber)
      .limit(1);

    if (fetchError) {
      console.error("[JobService.updateStage] fetch error:", fetchError);
      return { error: fetchError };
    }

    // Handle both array (from .limit(1)) and object cases
    let currentRow: JobRow | undefined;
    if (Array.isArray(current)) {
      if (current.length > 1) {
        console.warn(`[JobService.updateStage] Multiple jobs found for ${column}=${jobNumber}, using first match.`);
      }
      currentRow = current[0] as JobRow | undefined;
    } else {
      currentRow = current as JobRow | undefined;
    }

    if (!currentRow) {
      console.warn("[JobService.updateStage] No job found for", column, jobNumber);
      return { error: new Error("Job not found") };
    }

    const currentJob = toUiJob(currentRow);
    const patchAsJob: Partial<Job> = {
      currentStage: patch.currentStage,
      dispatchStatus: patch.dispatchStatus as Job["dispatchStatus"] | undefined,
      assignedCrew: patch.assignedCrew,
      assignedSubcontractor: patch.assignedSubcontractor,
      crewId: patch.crewId,
      schedulingStatus: patch.schedulingStatus as Job["schedulingStatus"] | undefined,
    };
    const installGuardError = await assertInstallStagePrereqs(
      String(currentRow.id ?? ""),
      currentJob,
      patchAsJob,
    );
    if (installGuardError) {
      console.warn("[JobService.updateStage] install transition blocked:", installGuardError.message);
      return { error: installGuardError };
    }

    const mergedMetadata = { ...(currentRow.metadata ?? {}), ...patch };
    const updatePayload: Record<string, unknown> = { metadata: mergedMetadata };
    if (patch.currentStage) updatePayload.current_stage = patch.currentStage;
    if ("crewId" in patch) {
      const crewId = String(patch.crewId ?? "").trim();
      updatePayload.crew_id = isUuid(crewId) ? crewId : null;
    }

    const { error } = await supabase
      .from("jobs")
      .update(updatePayload)
      .eq(column, jobNumber);

    if (error) console.error("[JobService.updateStage] update error:", error);
    if (!error) {
      const jobUuid = String(currentRow.id ?? "");
      if (patch.currentStage === "In Progress") {
        void SubcontractorAssignmentService.advanceToActive(jobUuid);
      }
      if (patch.currentStage === "Completed") {
        void markJobMaterialsInstalled(jobUuid);
        void SubcontractorAssignmentService.advanceToComplete(jobUuid);
      }
    }
    return { error };
  },

  async delete(id: string): Promise<{ error: unknown }> {
    const column = isUuid(id) ? "id" : "job_number";
    const { error } = await supabase.from("jobs").delete().eq(column, id);
    return { error };
  },

  async bulkInsert(jobs: Job[]): Promise<{ count: number; error: unknown }> {
    const organizationId = await getCurrentOrganizationId();
    const rows = jobs.map((job) => ({
      ...toDbJobCreate(job),
      organization_id: organizationId,
      source_meta: job.sourceMeta ?? null,
      title: deriveJobTitle(job),
    }));
    const { data, error } = await supabase
      .from("jobs")
      .upsert(rows, { onConflict: "job_number" })
      .select("id");
    return { count: data?.length ?? 0, error };
  },

  async unassignCrew(jobId: string, dateIso: string): Promise<boolean> {
    // assignedCrew/assignedSubcontractor/assignmentType live in metadata JSONB —
    // must go through JobService.update so the mapper merges them correctly.
    const { error } = await JobService.update(jobId, {
      assignedCrew: "",
      crewId: "",
      assignedSubcontractor: "",
      assignmentType: undefined,
      scheduledInstallDate: dateIso,
    });
    if (error) {
      console.error("[JobService.unassignCrew] failed:", JSON.stringify(error));
      return false;
    }
    return true;
  },
};
